import React from 'react';
import {
  CartPayment,
  CoreError,
  MULTI_PAYMENT_STATUSES,
  MULTI_PAYMENT_STEP_STATUSES,
  MULTI_PAYMENT_STEP_TYPES,
  MultiPayment,
  PAYMENT_PROVIDERS,
  RESERVATION_PAYMENT_TYPES,
  ReservationPayment,
  getErrorMessage,
  STATUSES,
  useStatus,
  QUERY_CACHE_KEYS,
} from '@chekin/core';
import {useTranslation} from 'react-i18next';
import type {TFunction} from 'i18next';
import {StripeError} from '@stripe/stripe-js';
import {usePaymentSettings} from './usePaymentSettings';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {useCheckExpiredPayments} from './useCheckExpiredPayments';
import {useStripePaymentElement} from './useStripePaymentElement';
import {useWebsocketListener} from './useWebsocketListener';
import type {WebsocketContextProps as ContextProps} from '../websocket/WebsocketContext';
import {usePaymentsEnv} from '../PaymentsEnv';
import {API, CreateMultiPaymentPayload, WS_EVENT_TYPES} from '@chekin/api';
import {fetcher} from '../../../utils/fetcher';
import {safeCaptureException} from '../../../utils/sentry';
import {useEvent} from '@chekinapp/ui';

const trimClearMessage = (message: string) => {
  if (message?.includes('Request')) {
    return message.substring(message.indexOf(':') + 2);
  }

  return message;
};

const RetryCheckManualPaymentStatusesMs = 2000;
const maxFallbackRetries = 20;

const getMultiPaymentPayload = (
  reservation_id = '',
  payments: CartPayment[],
  is_checkout_reservation = false,
  t: TFunction,
): CreateMultiPaymentPayload => {
  const paymentsIds = payments
    .filter(singleItem => {
      return ![
        RESERVATION_PAYMENT_TYPES.damageProtection,
        RESERVATION_PAYMENT_TYPES.securityDeposit,
      ].includes(singleItem.obj_type);
    })
    .map(singlePayment => singlePayment.id);

  const hasSecurityDeposit = payments.some(
    singlePayment => singlePayment.obj_type === RESERVATION_PAYMENT_TYPES.securityDeposit,
  );
  const hasDamageProtection = payments.some(
    singlePayment =>
      singlePayment.obj_type === RESERVATION_PAYMENT_TYPES.damageProtection,
  );
  const hasTaxes = payments.some(
    singlePayment => singlePayment.obj_type === RESERVATION_PAYMENT_TYPES.touristTaxes,
  );

  if (hasSecurityDeposit && hasDamageProtection) {
    throw new Error(t('cannot_choose_both_deposits_error'));
  }

  return {
    reservation: reservation_id,
    requested_specification: {
      requested_reservation_payment_ids: paymentsIds,
      is_deposit_payment_requested: hasSecurityDeposit,
      is_damage_protection_payment_requested: hasDamageProtection,
      is_taxes_payment_requested: hasTaxes,
      is_online_check_out_flow: is_checkout_reservation,
    },
  };
};

type PayProps = {
  payments: CartPayment[];
};

type UseMultiPaymentsProps = Pick<UsePaymentProps, 'onPaymentExpired'> & {
  onPaymentError: ErrorPaymentHandler;
  onPaymentCreated: (multiPayment: MultiPayment) => void;
};
function useMultiPayments({
  onPaymentError,
  onPaymentExpired,
  onPaymentCreated,
}: UseMultiPaymentsProps) {
  const {t} = useTranslation();
  const {reservationId, isCheckoutReservation = false} = usePaymentsEnv();

  const {mutate: mutateMultiPayment} = useMutation<
    MultiPayment,
    StripeError,
    CreateMultiPaymentPayload
  >({
    mutationFn: payload => API.payments.createMultiPayment(payload),
    onError: e => onPaymentError(e),
    onSuccess: onPaymentCreated,
  });

  const {checkExpiredPayments} = useCheckExpiredPayments({
    onExpired: onPaymentExpired,
    onError: onPaymentError,
  });

  const createPayment = React.useCallback(
    async (payments: CartPayment[]) => {
      const payload = getMultiPaymentPayload(
        reservationId,
        payments,
        isCheckoutReservation,
        t,
      );

      const hasExpiredPayments = await fetcher(checkExpiredPayments);
      if (hasExpiredPayments) return;
      mutateMultiPayment(payload);
    },
    [checkExpiredPayments, mutateMultiPayment, reservationId, isCheckoutReservation, t],
  );

  return {createPayment};
}

type ErrorPaymentHandler = (
  error: Error | CoreError | StripeError | string,
  multiPayment?: MultiPayment,
) => void;
export type UsePaymentProps = {
  onPaymentSuccess?: (multiPayment: MultiPayment) => void;
  onPaymentError: (
    error: Error | CoreError | StripeError | string,
    errorMsg?: string,
    multiPayment?: MultiPayment,
  ) => void;
  onStripeSubmit?: (payment: MultiPayment) => void;
  onPaymentExpired?: (expiredPayments: ReservationPayment[]) => void;
  onDataRequired?: (multiPayment: MultiPayment) => void;
  onProgress?: (multiPayment: MultiPayment) => void;
  onBankConfirmation?: (isConfirming: boolean) => void;
};

type UsePaymentReturnType = {
  isLoading: boolean;
  pay: (details: PayProps) => void;
  resumeListening: (multiPaymentId: string) => void;
};

function usePayment(props: UsePaymentProps): UsePaymentReturnType {
  const {t} = useTranslation();
  const queryClient = useQueryClient();
  const onPaymentSuccess = useEvent(props.onPaymentSuccess);
  const onPaymentError = useEvent(props.onPaymentError);
  const onPaymentExpired = useEvent(props.onPaymentExpired);
  const onStripeSubmit = useEvent(props.onStripeSubmit);
  const onDataRequired = useEvent(props.onDataRequired);
  const onProgress = useEvent(props.onProgress);
  const onBankConfirmation = useEvent(props.onBankConfirmation);
  const fallbackCalledTimes = React.useRef(0);
  const isSubmitDisabledRef = React.useRef<boolean>(false);
  const {isLoading, setStatus} = useStatus();
  const {paymentProvider} = usePaymentSettings();

  const {setListeningHandlers, start, reset} = useWebsocketListener({
    time: RetryCheckManualPaymentStatusesMs,
    autoReset: false,
    once: false,
  });

  const handleSuccess = React.useCallback(
    async (multiPayment: MultiPayment) => {
      await queryClient.refetchQueries({
        queryKey: [QUERY_CACHE_KEYS.paymentsCart],
      });
      void queryClient.refetchQueries({
        queryKey: [QUERY_CACHE_KEYS.summary],
      });
      void queryClient.invalidateQueries({
        queryKey: [QUERY_CACHE_KEYS.reservation],
      });
      void queryClient.invalidateQueries({
        queryKey: [QUERY_CACHE_KEYS.insuranceSetup],
      });
      void queryClient.invalidateQueries({
        queryKey: [QUERY_CACHE_KEYS.orderHistory],
      });
      void queryClient.invalidateQueries({
        queryKey: [QUERY_CACHE_KEYS.deals],
        refetchType: 'all',
      });

      setStatus(STATUSES.success);
      onPaymentSuccess(multiPayment);
    },
    [onPaymentSuccess, queryClient, setStatus],
  );

  const handlePaymentExpired = async (expiredPayments: ReservationPayment[]) => {
    setStatus(STATUSES.idle);
    await queryClient.refetchQueries({
      queryKey: [QUERY_CACHE_KEYS.paymentsCart],
    });
    void queryClient.refetchQueries({
      queryKey: [QUERY_CACHE_KEYS.summary],
    });
    onPaymentExpired?.(expiredPayments);
  };

  const handlePaymentError = React.useCallback(
    async (
      error: StripeError | Error | CoreError | string,
      multiPayment?: MultiPayment,
    ) => {
      reset();
      const defaultErrorMessage = t('bank_authentication_process_has_failed');
      await queryClient.refetchQueries({
        queryKey: [QUERY_CACHE_KEYS.paymentsCart],
      });
      void queryClient.invalidateQueries({
        queryKey: [QUERY_CACHE_KEYS.insuranceSetup],
      });
      const errorMessage = getErrorMessage(error);
      onPaymentError(
        error || defaultErrorMessage,
        errorMessage || defaultErrorMessage,
        multiPayment,
      );

      setStatus(STATUSES.idle);
    },
    [onPaymentError, queryClient, reset, setStatus, t],
  );

  const handleCancel = React.useCallback(async () => {
    setStatus(STATUSES.idle);
  }, [setStatus]);

  const {confirmPaymentSetup, submitPaymentElement} = useStripePaymentElement(
    handlePaymentError,
    handleCancel,
  );

  const listenMultiPaymentWSEvents = React.useCallback(
    (
      paymentId: string,
      statusesHandler: (
        multiPayment: MultiPayment | null,
        error?: Error | StripeError,
      ) => void,
    ) => {
      setListeningHandlers(
        {
          [WS_EVENT_TYPES.multiPaymentUpdated]: (ws: ContextProps<MultiPayment>) => {
            const instanceId = ws?.message?.instance_id;

            if (!paymentId || paymentId !== instanceId) {
              return;
            }

            const eventMsg = ws.message;
            statusesHandler(eventMsg);
          },
        },
        async () => {
          if (paymentId) {
            try {
              const data = await fetcher(API.payments.getMultiPayment, paymentId);
              fallbackCalledTimes.current = fallbackCalledTimes.current + 1;
              statusesHandler(data);
            } catch (error) {
              safeCaptureException(error, 'multiPayment polling');
              statusesHandler(null, error as Error);
            }
          }
        },
      );

      start();
    },
    [setListeningHandlers, start],
  );

  const handleWsStatuses = React.useCallback(
    async (multiPayment: MultiPayment | null, error?: Error | StripeError) => {
      if (error) {
        void handlePaymentError(error);
        reset();
        return;
      }
      if (!multiPayment) {
        return;
      }

      onProgress(multiPayment);

      if (fallbackCalledTimes.current > maxFallbackRetries) {
        reset();
        void handlePaymentError(t('number_of_calls_limited'));
        return;
      }

      switch (multiPayment.status) {
        case MULTI_PAYMENT_STATUSES.complete:
          void handleSuccess(multiPayment);
          reset();
          return;
        case MULTI_PAYMENT_STATUSES.failed:
          const statusDetails =
            multiPayment.current_step?.status === MULTI_PAYMENT_STEP_STATUSES.error
              ? trimClearMessage(multiPayment.current_step?.status_details)
              : multiPayment.status_details;
          void handlePaymentError(statusDetails, multiPayment);
          reset();
          return;
      }

      if (multiPayment.current_step.status === MULTI_PAYMENT_STEP_STATUSES.dataRequired) {
        if (paymentProvider === PAYMENT_PROVIDERS.addon) {
          reset();
          onDataRequired?.(multiPayment);
          // Don't start listening here - will be resumed after HPP form submission
          return;
        } else {
          const foundStepWithClientSecret = multiPayment.steps.find(
            step => step.type === MULTI_PAYMENT_STEP_TYPES.paymentSetupInit,
          );
          const clientSecret = foundStepWithClientSecret?.metadata.external_client_secret;
          if (clientSecret) {
            reset();
            onBankConfirmation(true);
            const intent = await confirmPaymentSetup(clientSecret);
            onBankConfirmation(false);
            if (intent?.error) {
              return;
            }

            listenMultiPaymentWSEvents(multiPayment.id, handleWsStatuses);
            return;
          }
        }
      }
    },
    [
      confirmPaymentSetup,
      handlePaymentError,
      listenMultiPaymentWSEvents,
      paymentProvider,
      reset,
      handleSuccess,
      t,
      onDataRequired,
      onProgress,
      onBankConfirmation,
    ],
  );

  const handlePaymentCreated = React.useCallback(
    (payment: MultiPayment) => {
      const paymentId = payment?.id;
      if (!paymentId) return;
      listenMultiPaymentWSEvents(paymentId, handleWsStatuses);
      onStripeSubmit?.(payment);
      // Waiting for WS_EVENT_TYPES.multiPayment or results of the pulling
    },
    [handleWsStatuses, listenMultiPaymentWSEvents, onStripeSubmit],
  );

  const {createPayment} = useMultiPayments({
    onPaymentError: handlePaymentError,
    onPaymentExpired: handlePaymentExpired,
    onPaymentCreated: handlePaymentCreated,
  });

  const handleStripeSubmit = React.useCallback(
    async ({payments}: PayProps) => {
      fallbackCalledTimes.current = 0;
      setStatus(STATUSES.loading);

      const isValid = await submitPaymentElement();
      if (!isValid) return;

      void createPayment(payments);
      // Next handlePaymentCreated
    },
    [createPayment, submitPaymentElement, setStatus],
  );

  const handleAddonSubmit = React.useCallback(
    async ({payments}: PayProps) => {
      fallbackCalledTimes.current = 0;
      setStatus(STATUSES.loading);

      void createPayment(payments);
      // Next handlePaymentCreated
    },
    [createPayment, setStatus],
  );

  const singleton = React.useCallback(
    (wrapper: (props: PayProps) => void) => (props: PayProps) => {
      if (isSubmitDisabledRef.current) return;
      isSubmitDisabledRef.current = true;
      wrapper(props);
      isSubmitDisabledRef.current = false;
    },
    [],
  );

  const providersEventHandlersMapping = React.useMemo(
    () => ({
      [PAYMENT_PROVIDERS.stripe]: singleton(handleStripeSubmit),
      [PAYMENT_PROVIDERS.paycomet]: singleton(() => {}),
      [PAYMENT_PROVIDERS.addon]: singleton(handleAddonSubmit),
    }),
    [handleStripeSubmit, handleAddonSubmit, singleton],
  );

  const pay = React.useCallback(
    (data: PayProps) => providersEventHandlersMapping[paymentProvider](data),
    [paymentProvider, providersEventHandlersMapping],
  );

  const resumeListening = React.useCallback(
    (multiPaymentId: string) => {
      if (!multiPaymentId) return;
      fallbackCalledTimes.current = 0;
      setStatus(STATUSES.loading);
      listenMultiPaymentWSEvents(multiPaymentId, handleWsStatuses);
    },
    [listenMultiPaymentWSEvents, handleWsStatuses, setStatus],
  );

  return {isLoading, pay, resumeListening};
}

export {usePayment};
